// Audio volume controls for the library

import React from 'react';
import { Volume2Icon } from 'lucide-react';
import SidebarPanelItem from '../SidebarPanelItem';
import {
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
} from '../../../components/ui/popover';
import { Separator } from '../../../components/ui/separator';
import { useSidebar } from '../../../components/ui/sidebar';
import VolumeSliderGroup from '@/components/media/VolumeSliderGroup';
import { useLibraryDialog } from '../hooks';
import { useAudioLevels } from '../../../hooks';
import type { UserAuthFields } from '@airhead-games/shared/types';

type VolumeControlsMenuProps = {
  user: UserAuthFields | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const VolumeControlsMenu: React.FC<VolumeControlsMenuProps> = ({
  user,
  open,
  onOpenChange,
}) => {
  const { isMobile, state } = useSidebar();
  const collapsedDesktop = !isMobile && state === 'collapsed';
  const { openDialog } = useLibraryDialog();
  const { audioLevels, setLevel } = useAudioLevels();

  const handleGuestAudioAttempt = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (user) {
      return;
    }
    event.preventDefault();
    openDialog('login');
  };

  const content = (
    <>
      <PopoverHeader>
        <PopoverTitle>Volume Controls</PopoverTitle>
        <PopoverDescription>Set your preferred audio mix.</PopoverDescription>
      </PopoverHeader>
      <Separator />
      <div onPointerDown={handleGuestAudioAttempt} className="pt-0">
        <VolumeSliderGroup
          disabled={!user}
          speechVolume={Math.round(audioLevels.speechVolume * 100)}
          musicVolume={Math.round(audioLevels.musicVolume * 100)}
          sfxVolume={Math.round(audioLevels.sfxVolume * 100)}
          onSpeechVolumeChange={(v) => setLevel('speechVolume', v / 100)}
          onMusicVolumeChange={(v) => setLevel('musicVolume', v / 100)}
          onSfxVolumeChange={(v) => setLevel('sfxVolume', v / 100)}
        />
      </div>
    </>
  );

  return (
    <SidebarPanelItem
      isMobile={isMobile}
      collapsedDesktop={collapsedDesktop}
      open={open}
      onOpenChange={onOpenChange}
      title="Volume Controls"
      icon={<Volume2Icon />}
      label="Volume Controls"
      isActive={open}
    >
      {content}
    </SidebarPanelItem>
  );
};

export default VolumeControlsMenu;
